import AnimationsManager from 'src/sdk-components/AnimationsManager';
import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, LoadingState, Outputs, SceneComponent } from './types';
import { CachedGeometry } from 'src/helpers/geometry';
import { waitForUserInteraction } from 'src/utils';

export class GLTFLoaderComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private model: any = null;

  private mixer: any = null;

  private clipAction: any = null;

  public clipActions: any = [];

  public animate = false;

  public activeAnimation = null;

  public animateSpeed = 1;

  public audio;

  //@ts-ignore
  private animationsManager: AnimationsManager | null;

  inputs: Inputs = {
    url: null,
    visible: true,
    colliderEnabled: true,
    animationEnabled: true,
    enableHoverAnimation: false,
    hideOnAnimationFinished: false,
    enableIdleAnimation: false,
    animationTriggerEnabled: false,
    audioEnabled: false,
    audioSrc: null,
    animationLoop: true,
    flatShading: false,
    adminPanelAccess: false,
    castShadow: true,
    scale: { x: 1, y: 1, z: 1 }
  };

  outputs = {
    loadingState: LoadingState.Idle
  } as Outputs;

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    if (this.inputs.adminPanelAccess) {
      this.animate = true;
    }
    this.loadModel();
  }

  onDestroy() {
    this.destroy();
  }

  destroy() {
    if (this.model) {
      disposeMesh(this.model);
      this.model = null;
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    if (!this.model) {
      return;
    }
    if (this.root && this.root.parent) {
      this.root.parent.visible = this.inputs.visible;
    }
  }

  private resizeImage = (image, size) => {
    const canvas = document.createElementNS('http://www.w3.org/1999/xhtml', 'canvas');
    // @ts-ignore
    canvas.width = size;
    // @ts-ignore
    canvas.height = size;
    // @ts-ignore
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, size, size);
    return canvas;
  };

  private repairTextures = (obj, size) => {
    //Encoding fix for post processing
    if (!obj.material.map && obj.material.color) obj.material.color.convertLinearToSRGB();
    if (obj.material.map) obj.material.map.encoding = this.context.three.LinearEncoding;
    if (obj.material.map && obj.material.map.image) {
      obj.material.map.image = this.resizeImage(obj.material.map.image, size);
      obj.material.map.needsUpdate = true;
    } else if (obj.material.length > 1) {
      obj.material.forEach((m) => {
        if (m.map && m.map.image) {
          m.map.image = this.resizeImage(m.map.image, size);
          m.map.needsUpdate = true;
        }
      });
    }
    if (obj.material.aoMap && obj.material.aoMap.image) {
      obj.material.aoMap.image = this.resizeImage(obj.material.aoMap.image, size);
    } else if (obj.material.length > 1) {
      obj.material.forEach((m) => {
        if (m.aoMap && m.aoMap.image) {
          m.aoMap.image = this.resizeImage(m.aoMap.image, size);
        }
      });
    }
    if (obj.material.normalMap && obj.material.normalMap.image) {
      obj.material.normalMap.image = this.resizeImage(obj.material.normalMap.image, size);
    } else if (obj.material.length > 1) {
      obj.material.forEach((m) => {
        if (m.normalMap && m.normalMap.image) {
          m.normalMap.image = this.resizeImage(m.normalMap.image, size);
        }
      });
    }
    if (obj.material.metalnessMap && obj.material.metalnessMap.image) {
      obj.material.metalnessMap.image = this.resizeImage(obj.material.metalnessMap.image, size);
    } else if (obj.material.length > 1) {
      obj.material.forEach((m) => {
        if (m.metalnessMap && m.metalnessMap.image) {
          m.metalnessMap.image = this.resizeImage(m.metalnessMap.image, size);
        }
      });
    }
  };

  // private blobToBase64 = (blob, callback) => {
  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     const dataUrl = reader.result;
  //     const base64 = (dataUrl as string).split(',')[1];
  //     callback(base64);
  //   };
  //   reader.readAsDataURL(blob);
  // };

  onTick(delta: number) {
    if (this.mixer) {
      this.mixer.update(delta / 1000);
    }
  }

  private loadAsync = async (THREE, url: string) => {
    if (url.includes('.gltf') || url.includes('.glb'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.GLTFLoader();
        loader.load(
          url,
          (gltf) => resolve(gltf),
          () => null,
          (err) => reject(err)
        );
      });
    if (url.includes('.fbx'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.FBXLoader();
        loader.load(
          url,
          (model) => resolve(model),
          () => null,
          (err) => reject(err)
        );
      });
  };

  protected async loadAnimation(THREE, url: string) {
    return this.loadAsync(THREE, url);
  }

  private loadModel = () => {
    const THREE = this.context.three;
    if (this.model) this.destroy();

    if (!this.inputs.url) {
      return;
    }

    const manager = new THREE.LoadingManager();
    manager.onError = function (url) {
      console.log('There was an error loading ' + url);
    };
    // Instantiate a loader
    const loader = new THREE.GLTFLoader(manager);
    this.outputs.loadingState = LoadingState.Loading;
    // Load a glTF resource
    loader.load(
      // resource URL
      this.inputs.url,
      // called when the resource is loaded
      async (gltf: any) => {
        this.model = gltf.scene;

        const { castShadow } = this.inputs;
        this.model.traverse((obj: any) => {
          if (obj == null) {
            console.log('[Err] GLTFLoader :: Null object found on ', obj);
            this.model.children[obj] = new THREE.Object3D();
            return;
          }
          if (!obj.isMesh) return;
          this.repairTextures(obj, 512);
          obj.castShadow = castShadow;
          obj.receiveShadow = castShadow;
          if (obj.geometry) {
            if (!obj.geometry.isBufferGeometry) {
              if (obj.geometry.isGeometry) {
                const ogeo = obj.geometry;
                obj.geometry = new THREE.BufferGeometry().fromGeometry(ogeo);
                ogeo.dispose();
                // CachedGeometry.CreateBVHProxy(obj.geometry);
              }
            } else {
              // CachedGeometry.CreateBVHProxy(obj.geometry);
            }
          }
          if (obj.material == null) {
            console.log('[Err] GLTFLoader :: Null material found on ', obj);
            obj.material = new THREE.MeshBasicMaterial();
            return;
          }
          if (obj.material.transmission) obj.material.transmission = 0;
          if (Array.isArray(obj.material)) {
            for (let i = 0; i < obj.material.length; i += 1) {
              obj.material[i] = new THREE.MeshBasicMaterial();
              if (obj.material[i] == null) {
                console.log('[Err] GLTFLoader :: Null material found on ', obj.material[i]);
                obj.material[i] = new THREE.MeshBasicMaterial();
                continue;
              }
              if (obj.material[i].metalness >= 0.9) {
                obj.material[i].metalness = 0.7;
                obj.material[i].roughness = 0.4;
              }
            }
            if (obj.material == null) {
              console.log('[Err] GLTFLoader :: Null material found on ', obj);
              obj.material = new THREE.MeshBasicMaterial();
              return;
            }
          } else {
            if (obj.material.metalness >= 0.9) {
              obj.material.metalness = 0.7;
              obj.material.roughness = 0.4;
            }
          }
        });
        if (
          gltf.animations?.length &&
          gltf.animations?.length > 0 &&
          this.inputs.visible &&
          this.inputs.animationEnabled
        ) {
          this.mixer = new THREE.AnimationMixer(gltf.scene);
          this.clipAction = this.mixer.clipAction(gltf.animations[0]);

          gltf.animations.forEach((action) => {
            const clipAction = this.mixer.clipAction(action);
            clipAction.clampWhenFinished = true;
            this.clipActions.push(clipAction);
          });

          if (this.inputs.audioSrc && this.inputs.audioEnabled && !this.inputs.adminPanelAccess) {
            this.audio = new Audio(this.inputs.audioSrc);
          }

          if (this.inputs.enableIdleAnimation) {
            const idleAnimation = await this.loadAnimation(THREE, '../../images/Idle.fbx');
            idleAnimation.animations[0].name = 'idle';
            const idleAnimationAction = this.mixer.clipAction(idleAnimation.animations[0]);
            this.clipActions.push(idleAnimationAction);
            if (!this.inputs.hideOnAnimationFinished) {
              this.mixer.addEventListener('finished', () => {
                this.fadeOrPlayAnimation(idleAnimationAction);
              });
            }
            if (this.inputs.animationTriggerEnabled) {
              idleAnimationAction.reset().play();
              this.activeAnimation = idleAnimationAction._clip.name;
            }
          }

          if (!this.inputs.animationLoop) {
            this.clipAction.setLoop(THREE.LoopOnce);
            if (this.inputs.hideOnAnimationFinished && !this.inputs.adminPanelAccess)
              this.mixer.addEventListener('finished', () => {
                this.root.visible = false;
              });
          }
          // if the object has trigger then no need to play at a start
          if (!this.inputs.animationTriggerEnabled) {
            this.fadeOrPlayAnimation(this.clipAction);
            if (this.audio && this.inputs.audioEnabled) {
              this.audio.play().catch(() => {
                waitForUserInteraction(() => this.audio.play().catch(() => null));
              });
            }
          }
        }
        if (
          !this.inputs.animationEnabled &&
          this.inputs.visible &&
          this.inputs.enableIdleAnimation &&
          !this.inputs.hideOnAnimationFinished
        ) {
          this.mixer = new THREE.AnimationMixer(gltf.scene);
          const idleAnimation = await this.loadAnimation(THREE, '../../images/Idle.fbx');
          idleAnimation.animations[0].name = 'idle';
          const idleAnimationAction = this.mixer.clipAction(idleAnimation.animations[0]);
          this.clipActions.push(idleAnimationAction);
          this.fadeOrPlayAnimation(idleAnimationAction);
        }
        if (this.root && this.root.parent) {
          this.root.parent.visible = this.inputs.visible;
        }

        this.pivot?.add(this.model);
        if (this.inputs.adminPanelAccess) {
          this.outputs.collider = this.root;
        } else {
          if (this.inputs.colliderEnabled || this.inputs.enableHoverAnimation) {
            try {
              const scene = this.root.parent.parent;
              this.root.updateMatrixWorld(true);

              const boundingBox = new THREE.Box3().setFromObject(this.model, true);
              // Get the size of the bounding box
              const boxSize = new THREE.Vector3();
              const boxCenter = new THREE.Vector3();
              boundingBox.getSize(boxSize);
              boundingBox.getCenter(boxCenter);

              const geometry = CachedGeometry.CreateColliderMeshBoundingBoxFromScene(
                THREE,
                this.outputs.objectRoot
              );
              const material = new THREE.MeshBasicMaterial({
                color: 'white',
                transparent: true,
                opacity: 0
              });

              const collider = new THREE.Mesh(geometry, material);
              collider.position.copy(boxCenter);

              scene.add(collider);
              this.outputs.collider = collider;
            } catch (e) {
              console.log('[Err] No collider for ', this.model);
            }
          }
        }

        // const lod = new THREE.LOD();
        // lod.addLevel(this.model, 0);
        // this.compressedModel = this.model.clone();
        // this.compress(this.compressedModel);
        // lod.addLevel(this.compressedModel, 10);
        // this.pivot?.add(lod);

        this.outputs.loadingState = LoadingState.Loaded;
        // console.log(' > model', this.estimateKBytesUsed(this.model) + ' kb');
        // console.log(' > compressedModel', this.estimateKBytesUsed(this.compressedModel) + ' kb');
      },
      // called while loading is progressing
      (/* xhr */) => {
        // console.log((xhr.loaded / xhr.total * 100) + '% loaded');
        this.outputs.loadingState = LoadingState.Loading;
      },
      // called when loading has errors
      (error: ErrorEvent) => {
        console.error('Failed to load GLTF model:', error);
        this.outputs.loadingState = LoadingState.Error;
      }
    );
  };

  fadeOrPlayAnimation(clipAction, duration = 0) {
    if (this.activeAnimation) {
      const currentAnimationAction = this.clipActions.find(
        (action) => action._clip.name === this.activeAnimation
      );
      if (currentAnimationAction)
        clipAction.crossFadeFrom(currentAnimationAction, duration, true).reset().play();
      else {
        console.log(
          '[Err]: activeAnimation not found in animation array despite activeAnimation being set, starting new animation manually'
        );
        clipAction.reset().play();
      }
      this.activeAnimation = clipAction._clip.name;
    } else {
      clipAction.reset().play();
      this.activeAnimation = clipAction._clip.name;
    }
  }

  toggleClipAnimation(play: boolean) {
    if (this.clipAction) {
      if (play) {
        // If the animation.time is > 0 then we are resuming the animation from a paused state.
        if (this.clipAction.time > 0) this.clipAction.paused = false;
        else this.fadeOrPlayAnimation(this.clipAction);

        if (this.audio && this.inputs.audioEnabled) this.audio.play();
      } else {
        // Before we had clipAction.stop() but stop resets the animation to the beginning and we need to be able to pause and resume it. TB-3126
        // this.clipAction.stop();
        this.clipAction.paused = true;
        this.activeAnimation = null;
        if (this.audio && this.inputs.audioEnabled) this.audio.pause();
      }
    }
  }
}

export const gltfLoaderComponentType = 'mp.gltfLoaderComponent';
export const makeGLTFLoaderComponent = (): any => new GLTFLoaderComponent();
